/* eslint-disable @typescript-eslint/no-explicit-any, react-refresh/only-export-components */
import { useChildMutations } from "@/features/leads/api/useChildMutations";
import { onInvalid } from "@/features/leads/contexts/FormLeadContext";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import useCurrentCourse from "@/features/universityAndCourse/api/useCurrentCourse";
import useCurrentIndustry from "@/hooks/globalapis/useCurrentIndustry";
import { useSyncronizer } from "@/hooks/useSyncronizer";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { CustomInputScopeSchema } from "@/types/general";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import {
  Control,
  UseFormRegister,
  FieldErrors,
  useForm,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import z from "zod";
import { setStartApplication } from "../stores/educationSlice";
import { setLeadCurrentTab } from "@/features/leads/stores/leadFunctionalSlice";
import { setIdentifierAndIdentifier } from "@/features/leads/stores/childSlice";
import usePipeline from "@/features/stage/api/usePipeline";

const ApplicaitionCreation = z.object({
  // --- IDENTITY ---
  name: z.string().min(1, "University name is required"),
  // --- CUSTOM ---
  customInputScopes: z.array(CustomInputScopeSchema).optional(),
  // --- FINANCIALS ---
  discountAmount: z.union([z.string(), z.number()]).optional(),
  addonAmount: z.union([z.string(), z.number()]).optional(),
});

type ApplicationFormData = z.infer<typeof ApplicaitionCreation>;

/* ---------------- TYPES ---------------- */

type FormEducationContextType = {
  lead: any;
  refetchLead: any;
  register: UseFormRegister<ApplicationFormData>;
  control: Control<ApplicationFormData>;
  errors: FieldErrors<ApplicationFormData>;
  handleSubmit: UseFormHandleSubmit<ApplicationFormData>;
  reset: (values?: Partial<ApplicationFormData>) => void;
  watch: UseFormWatch<ApplicationFormData>;
  getValues: UseFormGetValues<ApplicationFormData>;
  setValue: UseFormSetValue<ApplicationFormData>;
  setError: UseFormSetError<ApplicationFormData>;
  onSubmit: any;
  course: any;
  isAllowCustomName: boolean;
  moduleScopes: { nicheModelName: string; nicheItemId: number }[];
};

/* ---------------- CONTEXT ---------------- */

const FormEducationContext = createContext<FormEducationContextType | null>(
  null
);

/* ---------------- PROVIDER ---------------- */

export function EducationProvider({ children }: { children: ReactNode }) {
  const { lead, refetchLead, handleChildChange } = useActiveDataMutation();
  const { industry } = useCurrentIndustry();
  const dispatch = useAppDispatch();

  const { courseDetailId } = useAppSelector((state) => state.educationSlice);
  const { stageId: previousStageId } = useAppSelector(
    (state) => state.childFunctional
  );
  const { pipeline } = usePipeline();
  const { course, refetch } = useCurrentCourse(courseDetailId);

  useSyncronizer(courseDetailId, () => refetch());

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = useForm<ApplicationFormData>({
    resolver: zodResolver(ApplicaitionCreation),
    mode: "onChange",
    defaultValues: {
      name: "",
      customInputScopes: [],
      discountAmount: "",
      addonAmount: "",
    },
  });
  const { createChild } = useChildMutations();

  const isAllowCustomName = lead?.stage?.isAllowCustomName ?? true;
  const childCount = lead?.child?.length || 0;

  const moduleScopes = useMemo(
    () =>
      course?.id
        ? [{ nicheModelName: "course" as const, nicheItemId: course.id }]
        : [],
    [course]
  );

  useEffect(() => {
    if (!course) return;
    // UNIVERSITY FIRST, then the course. A lead's parallel choices are read and
    // compared by institution ("which universities are we running?"), so the
    // part that distinguishes them belongs at the front — with the course
    // leading, three choices at one university all looked unrelated and the
    // university was the bit that got truncated away in narrow lists.
    //
    // Built from a filtered list rather than a template so a course with no
    // university doesn't render a dangling " - " (the old string always
    // interpolated the separator).
    const parts = [course.university?.name, course.name].filter(
      (part): part is string => typeof part === "string" && part.trim() !== "",
    );
    const autoName = parts.length
      ? `${parts.join(" - ")} ${childCount + 1}`
      : `Application ${childCount + 1}`;
    setValue("name", autoName);
  }, [course, childCount, setValue]);

  const onSubmit = useCallback(
    (data: any) => {
      const courseFee = Number(course?.minFee) || 0;
      const addon = Number(data.addonAmount) || 0;
      const discount = Number(data.discountAmount) || 0;
      const totalDealValue = courseFee + addon;
      const netAmount = Math.max(0, totalDealValue - discount);

      data.moduleScopes = [
        {
          nicheModelName: "course",
          nicheItemId: course.id,
        },
      ];

      data.leadId = lead.id;

      if (totalDealValue > 0) {
        data.financials = {
          totalDealValue,
          discountType: "amount",
          discountValue: discount,
          discountAmount: discount,
          netAmount,
          addonAmount: addon,
        };
      }

      delete data.discountAmount;
      delete data.addonAmount;

      createChild.mutate(data, {
        onSuccess: (res) => {
          const child = res?.data?.child;
          const childId = child?.id;
          handleChildChange(childId);
          dispatch(setStartApplication(false));

          const targetStageId = child?.stageId || previousStageId;
          const stage = pipeline?.find((s: any) => s.id === targetStageId);
          if (stage?.name) {
            dispatch(setLeadCurrentTab(stage.name));
            dispatch(
              setIdentifierAndIdentifier({
                stageId: stage.id,
                unique_identifier: null,
              })
            );
          }

          refetchLead();
        },
        onError: () => {
          // handle error
        },
      });
    },
    [
      course,
      lead,
      createChild,
      handleChildChange,
      dispatch,
      previousStageId,
      pipeline,
      refetchLead,
    ],
  );

  const wrappedHandleSubmit = useCallback<UseFormHandleSubmit<any>>(
    (fn) => handleSubmit(fn, onInvalid),
    [handleSubmit],
  );

  const value = useMemo<FormEducationContextType>(
    () => ({
      lead,
      refetchLead,
      register,
      control,
      handleSubmit: wrappedHandleSubmit,
      onSubmit,
      reset,
      watch,
      errors,
      getValues,
      setValue,
      setError,
      course,
      isAllowCustomName,
      moduleScopes,
    }),
    [
      lead,
      refetchLead,
      register,
      control,
      wrappedHandleSubmit,
      onSubmit,
      reset,
      watch,
      errors,
      getValues,
      setValue,
      setError,
      course,
      isAllowCustomName,
      moduleScopes,
    ],
  );

  /** Study abroad (1) and general education (2) — same application / course flows */
  if (industry?.id !== 1 && industry?.id !== 2) return null;

  return (
    <FormEducationContext.Provider value={value}>
      {children}
    </FormEducationContext.Provider>
  );
}

/* ---------------- HOOK ---------------- */

export function useEducation(): FormEducationContextType {
  const context = useContext(FormEducationContext);

  if (!context) {
    throw new Error("useEducation must be used within StudyAbroadProvider");
  }

  return context;
}
